import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { Link } from "react-router-dom";

import {
  correctedToValue,
  correctionFieldLabel,
  correctionLineLabel,
  correctionPathLabel,
  correctionStateLabel,
  correctionValueLabel,
  isCorrectionClosedUnattributed,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";

type Props = {
  corrections: ManifestCorrectionRow[];
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
};

/**
 * The manifest corrections staff cited against this exceedance — the fixes that
 * account for it once resolved, or the ones it is parked on until they land.
 * Renders nothing unless the exceedance carries a MANIFEST_CORRECTION resolution,
 * which is the only way links exist.
 *
 * Dates are UTC to match the Manifest Review page, where the same corrections are
 * worked — deliberately, even though the rest of this drawer reads in the viewer's
 * zone. A correction should carry one date wherever staff see it.
 *
 * A cited correction that closes unattributed reopens the exceedance and drops its
 * links (`reconcileExceedancesOnDiscrepancyClose`), so that state is transient here
 * rather than something to warn about; the chip still reports it truthfully in the
 * window before the pipeline reconciles.
 */
export function ManifestCorrectionsTable({ corrections, tenantId }: Props) {
  if (corrections.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" component="h4">
        Manifest corrections
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Manifest</TableCell>
              <TableCell>Line</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Corrected to</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Corrected in</TableCell>
              <TableCell>Corrected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {corrections.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <MuiLink
                    component={Link}
                    to={`/o/${tenantId}/waste/manifests/${c.processedManifestId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {c.manifestTrackingNumber}
                  </MuiLink>
                  {c.shippedDate && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      shipped {getUtcDisplayDate(c.shippedDate, "med")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{correctionLineLabel(c)}</TableCell>
                <TableCell>{correctionFieldLabel(c.field)}</TableCell>
                <TableCell>{correctionValueLabel(correctedToValue(c))}</TableCell>
                <TableCell>
                  <StatusChip color={correctionChipColor(c)} label={correctionStateLabel(c)} />
                </TableCell>
                <TableCell>{correctionPathLabel(c.resolutionPath)}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {c.resolvedAt ? getUtcDisplayDate(c.resolvedAt, "med") : "—"}
                  {c.resolvedByName ? ` · ${c.resolvedByName}` : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Landed reads as done, an unattributed close as a problem, in flight as neutral. */
function correctionChipColor(row: ManifestCorrectionRow): "success" | "warning" | "info" {
  if (isCorrectionLanded(row)) return "success";
  if (isCorrectionClosedUnattributed(row)) return "warning";
  return "info";
}
